import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Clock } from "lucide-react";
import Seo from "../components/Seo";
import { InfinityLoop, SmileFigure, HouseHeart, UnitedFigures } from "../components/Illustrations";
import posts from "../data/blog.json";

const AUDIENCE_LABEL = {
  candidates: "For Candidates",
  employers: "For Employers",
  market: "Market",
};

export default function Home() {
  const featuredPosts = [...posts]
    .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt))
    .slice(0, 3);
  return (
    <div data-testid="page-home">
      <Seo path="/" image="/og-brand.png" />
      {/* HERO — stacked serif wordmark style with infinity loop */}
      <section className="pt-28 md:pt-36 pb-12 md:pb-16 bg-stone text-cream relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 relative">
          <p className="text-xs uppercase tracking-[0.3em] font-body font-medium text-mint mb-8 animate-fade-up">
            Technology recruitment, the human way · Aotearoa NZ
          </p>
          <h1 className="font-headings text-7xl sm:text-8xl md:text-9xl lg:text-[12rem] text-mint tracking-tight leading-[0.85] animate-fade-up delay-100">
            Unite
            <br />
            <span className="text-cream/95">Recruitment</span>
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-12 md:mt-16 items-end">
            <div className="lg:col-span-7">
              <p className="font-headings text-2xl md:text-3xl lg:text-4xl text-cream leading-snug animate-fade-up delay-200 max-w-2xl">
                We help candidates and employers{" "}
                <span className="text-mint italic">unite</span>.
              </p>
              <p className="mt-6 font-body text-base md:text-lg text-cream/75 max-w-xl leading-relaxed animate-fade-up delay-300">
                A boutique New Zealand technology recruitment partner. We take the time to
                understand the person on both sides of the table — then make the
                introduction that actually fits.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4 animate-fade-up delay-400">
                <Link
                  to="/find-a-job"
                  data-testid="hero-cta-candidate"
                  className="inline-flex items-center justify-center gap-2 bg-mint text-ink hover:bg-mint-deep px-7 py-3.5 rounded-full font-body font-medium transition-transform hover:-translate-y-0.5"
                >
                  I'm looking for a job
                  <ArrowUpRight size={18} />
                </Link>
                <Link
                  to="/find-a-candidate"
                  data-testid="hero-cta-employer"
                  className="inline-flex items-center justify-center gap-2 bg-transparent border border-mint/50 text-mint hover:bg-mint/10 px-7 py-3.5 rounded-full font-body font-medium transition-all"
                >
                  I'm hiring talent
                  <ArrowUpRight size={18} />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 animate-fade-in delay-500">
              <UnitedFigures className="w-full h-56 md:h-72" stroke="#C2FFE1" strokeWidth={5} />
            </div>
          </div>
        </div>

        {/* Infinity loop signature at bottom */}
        <div className="mt-16 md:mt-20 -mb-8 opacity-90">
          <InfinityLoop className="w-full h-32 md:h-44" stroke="#C2FFE1" strokeWidth={5} animate />
        </div>
      </section>

      {/* DUAL VALUE PROP — candidate (with smile figure) + employer (with house heart) */}
      <section className="py-20 md:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.25em] font-medium text-stone-dark mb-5">
              Two paths · one partnership
            </p>
            <h2 className="font-headings text-4xl md:text-5xl text-ink tracking-tight leading-tight">
              Whichever side you're on, we've{" "}
              <span className="hand-underline active">walked it</span>.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <div
              data-testid="value-card-candidate"
              className="bg-stone text-cream p-8 md:p-12 rounded-2xl relative overflow-hidden"
            >
              <SmileFigure
                className="absolute -right-10 -top-6 w-40 h-52 opacity-30"
                stroke="#C2FFE1"
                strokeWidth={4}
              />
              <p className="text-xs uppercase tracking-[0.25em] text-mint mb-4 relative">For candidates</p>
              <h3 className="font-headings text-3xl md:text-4xl mb-5 leading-tight relative">
                Not a CV in a stack. A career conversation.
              </h3>
              <ul className="space-y-3 font-body text-cream/85 mb-8 relative">
                {[
                  "Real coaching on your CV and interview",
                  "Roles that match what you actually want",
                  "Honest feedback at every stage",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-mint mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/find-a-job"
                className="inline-flex items-center gap-2 font-body font-medium text-mint link-underline relative"
              >
                Explore roles <ArrowUpRight size={16} />
              </Link>
            </div>

            <div
              data-testid="value-card-employer"
              className="bg-mint text-ink p-8 md:p-12 rounded-2xl relative overflow-hidden"
            >
              <HouseHeart
                className="absolute -right-16 -bottom-10 w-80 h-48 opacity-30"
                stroke="#2D3033"
                strokeWidth={4}
              />
              <p className="text-xs uppercase tracking-[0.25em] text-stone-dark mb-4 relative">For employers</p>
              <h3 className="font-headings text-3xl md:text-4xl mb-5 leading-tight relative">
                Shortlists you'd genuinely hire from.
              </h3>
              <ul className="space-y-3 font-body text-ink-700 mb-8 relative">
                {[
                  "Tailored search — no spray and pray",
                  "Briefed on your culture, not just your JD",
                  "Honest updates at every stage",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-ink mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/find-a-candidate"
                className="inline-flex items-center gap-2 font-body font-medium text-ink link-underline relative"
              >
                See how we work <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* INSIGHTS PREVIEW — real blog posts */}
      <section className="py-20 md:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-medium text-stone-dark mb-5">
                From the journal
              </p>
              <h2 className="font-headings text-4xl md:text-5xl text-ink tracking-tight leading-tight max-w-2xl">
                Ideas worth reading on hiring and finding work.
              </h2>
            </div>
            <Link
              to="/help"
              data-testid="insights-view-all"
              className="inline-flex items-center gap-2 font-body font-medium text-ink link-underline self-start md:self-end"
            >
              View all posts <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredPosts.map((p, idx) => (
              <Link
                key={p.slug}
                to={`/help/${p.slug}`}
                data-testid={`insight-card-${idx}`}
                className="bg-white p-8 rounded-2xl border border-ink-100 hover:shadow-md hover:-translate-y-1 transition-all cursor-pointer group flex flex-col"
              >
                <span className="inline-block self-start text-[10px] uppercase tracking-[0.25em] font-medium text-ink bg-mint px-3 py-1 rounded-full">
                  {AUDIENCE_LABEL[p.audience] || p.audience}
                </span>
                <h3 className="font-headings text-2xl text-ink mt-6 leading-tight group-hover:text-stone-dark transition-colors">
                  {p.title}
                </h3>
                <p className="mt-4 text-sm text-ink-700 font-body leading-relaxed flex-1">
                  {p.excerpt}
                </p>
                <p className="mt-5 text-xs text-stone-dark font-body flex items-center gap-1.5">
                  <Clock size={12} strokeWidth={1.5} /> {p.readingMinutes} min read
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
